import classNames from 'classnames';
import React, { useContext } from 'react';
import {
    transformLetterSpacingToApp,
    transformLetterSpacingToStage,
    transformLineSpacingToApp,
    transformLineSpacingToStage,
} from '@meitu/saas-editor-core/es/utils/transform-kit';
import { type IVectorLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { TextSpacingGroup } from '@/components/text-spacing-group';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { VectorPanelContext } from '../../context';
import { Tracker } from '../../utils';
import '@/styles/shared.less';
import { VectorLayer } from '@meitu/saas-editor-core/es/layers/vector-layer';

const FALLBACK_FONT_SIZE = 6;
const FALLBACK_STAGE_LINE_SPACING = 1.4;
const FALLBACK_STAGE_LETTER_SPACING = 0;

export const TextSpacing: React.FC = () => {
    const { layerId, recordId, isIntl } = useContext(VectorPanelContext)!;

    const editor = useEditor();

    const props = useListenActivePickLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as IVectorLayerProps;
        const { textInfo } = layerData;

        if (!textInfo) return null;

        const { lineSpacing, letterSpacing, fontSize } = textInfo;

        return {
            fontSize,
            lineSpacing,
            letterSpacing,
        };
    });

    const fontSize = props ? props.fontSize : FALLBACK_FONT_SIZE;
    const stageLetterSpacing = props ? props.letterSpacing : FALLBACK_STAGE_LETTER_SPACING;
    const stageLineSpacing = props ? props.lineSpacing : FALLBACK_STAGE_LINE_SPACING;

    const letterSpacing = ~~(Math.round(transformLetterSpacingToApp(stageLetterSpacing, fontSize)) / 10);
    const lineSpacing = ~~(Math.round(transformLineSpacingToApp(stageLineSpacing)) / 10);

    const handleChange = (kind: 'letterSpacing' | 'lineSpacing', value: number, options = { writeHistory: true }) => {
        if (!props) return;

        if (kind === 'letterSpacing') {
            const spacing = transformLetterSpacingToStage(value * 10, fontSize);
            editor.getInstance(VectorLayer).setLetterSpacing(layerId, spacing, options);
        } else {
            const spacing = transformLineSpacingToStage(value * 10);
            editor.getInstance(VectorLayer).setLineSpacing(layerId, spacing, options);
        }

        if (options.writeHistory) {
            Tracker.setSpacing({
                recordId,
                stageType: editor.stage.getType(),
                type: kind,
                value,
                isVectorLayer: true,
            });
        }
    };

    return (
        <div className={classNames('text-spacing', { intl: isIntl })}>
            <TextSpacingGroup
                letterSpacing={letterSpacing}
                lineSpacing={lineSpacing}
                onChange={(kind, value) => handleChange(kind, value, { writeHistory: false })}
                onAfterChange={(kind, value) => handleChange(kind, value, { writeHistory: true })}
            />
        </div>
    );
};
